import * as React from "react";
import { Button } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { Workload } from "../../../schema/types/workload/entity";
import { getWorkloadBasicTableItem, WorkloadBasicTableItem } from "../../../mst/kinds/workload.helpers";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { Table } from "../../../newcomponents/table/table";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { GvcColumn } from "../../../newcomponents/table/columns/wellKnown/gvcColumn";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { TableModal } from "../../../components/antd/TableModal";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { formSet, formSetValue, useCpFormAnyValue, useCpFormValue } from "../testNewForm/utils";
import { CpFormItem } from "../testNewForm/CpFormItem";
import { CpRadioGroup } from "../testNewForm/CpRadioGroup";
import { CpLabel } from "../testNewForm/CpLabel";
import { CpInput } from "../testNewForm/CpInput";
import { CpDescription } from "../testNewForm/CpDescription";
import { CpErrorWarning } from "../testNewForm/CpErrorWarning";
import { CpArrayObject } from "../testNewForm/CpArrayObject";
import { CpArrayString } from "../testNewForm/CpArrayString";
import { CpArrayItemNameValuePair } from "../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemSidecarJwtPathBasedAuthControl } from "../../workload/draft/CpArrayItemSidecarJwtPathBasedAuthControl";
import { CpArrayObjectSortable } from "../testNewForm/CpArrayObjectSortable";
import { store } from "../../../store";
import { cloneDeep, get } from "lodash";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { KindSelectState } from "../../../components/antd/kindselect/KindSelect";

interface Props {}
export const SidecarEnvoyEditable: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const { pathname } = useLocation();
  const isGvcCreate = React.useMemo(() => {
    return pathname.includes(`/gvc/-create/`);
  }, [pathname]);

  const type = useCpFormValue(`sidecar.envoy.type`);
  const custom = useCpFormValue(`sidecar.envoy.custom`);

  const { kind: addTableKind, id: addTableId } = useTableKindId("workload", "workload-auth-workloads");
  const qData = useTableItemQueryData<Workload>(addTableKind, {});

  const [selectedLinksToAdd, setSelectedLinksToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<WorkloadBasicTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, WorkloadBasicTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const workloads = useCpFormValue(`sidecar.envoy.jwt.excludedWorkloads`);
  React.useEffect(() => {}, [workloads]);
  const workloadTableItems: WorkloadBasicTableItem[] = React.useMemo(() => {
    return workloads.map((w) => getWorkloadBasicTableItem({ item: w.item, link: w.link }));
  }, [workloads, itemCache]);

  const onRemoveItem = (index: number) => {
    const form = store.getState().form.instances[id];
    const values = form.values;
    const _currentWorkloads = get(values, `sidecar.envoy.jwt.excludedWorkloads`);
    const currentWorkloads = cloneDeep(_currentWorkloads);
    currentWorkloads.splice(index, 1);
    formSetValue(id, `sidecar.envoy.jwt.excludedWorkloads`, currentWorkloads);
  };

  function onOkAddModal() {
    try {
      const form = store.getState().form.instances[id];
      const values = form.values;
      const _currentWorkloads = get(values, `sidecar.envoy.jwt.excludedWorkloads`);
      const currentWorkloads = cloneDeep(_currentWorkloads);
      for (const selectedLinkToAdd of selectedLinksToAdd) {
        const item = selectionItems.find((i) => i.selfLink === selectedLinkToAdd);
        if (item) {
          itemCache[selectedLinkToAdd] = item;
        }
        if (currentWorkloads.some((w) => w.link === selectedLinkToAdd)) {
          continue;
        }
        const parsedLink = parseLink(selectedLinkToAdd, { useInputCtx: true });
        const value: KindSelectState = {
          value: parsedLink.gvcRelative,
          link: selectedLinkToAdd,
        };
        if (item) {
          value.item = item;
        }
        currentWorkloads.push(value);
      }
      // currentWorkloads.sort();
      // TODO initialize this item cache on load for detail page
      setItemCache(itemCache);
      formSetValue(id, `sidecar.envoy.jwt.excludedWorkloads`, currentWorkloads);
      setSelectedLinksToAdd([]);
      setSelectionItems([]);
      setIsAddingItem(false);
    } catch (e) {
      console.log("fail on onokaddmodal", e.message);
    }
  }

  const addWorkloadTableItems: WorkloadBasicTableItem[] = qData.visibleItems.map((item) =>
    getWorkloadBasicTableItem({ item }),
  );

  return (
    <>
      <CpFormItem>
        <CpLabel name={`sidecar.envoy.type`}>Config Type</CpLabel>
        <CpRadioGroup
          name={`sidecar.envoy.type`}
          options={[
            { label: "None", value: "none" },
            { label: "JWT Auth", value: "jwt" },
            { label: "Custom Envoy", value: "custom" },
          ]}
        />
      </CpFormItem>
      {type === "custom" ? (
        <>
          <CodeEditor
            value={custom}
            setValue={(value) => formSetValue(id, `sidecar.envoy.custom`, value)}
            onChange={() => {
              const name = "sidecar.envoy.custom";
              formSet(id, `fieldStates.${name}.modified`, true);

              const form = store.getState().form.instances[id];
              const onChangeCounter = form.meta.onChangeCounter;
              formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
            }}
            onFocus={() => {
              const name = "sidecar.envoy.custom";
              formSet(id, `fieldStates.${name}.focused`, true);
              formSet(id, `fieldStates.${name}.visited`, true);
            }}
            onBlur={() => {
              const name = "sidecar.envoy.custom";
              formSet(id, `fieldStates.${name}.focused`, false);
              formSet(id, `fieldStates.${name}.touched`, true);

              const form = store.getState().form.instances[id];
              const onBlurCounter = form.meta.onBlurCounter;
              formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
            }}
            language="yaml"
            options={{ basicSetup: { lineNumbers: true } }}
          />
          <CpErrorWarning name="sidecar.envoy.custom" />
        </>
      ) : null}
      {type === "jwt" ? (
        <>
          <CpFormItem>
            <div className="flex gap-2">
              <div className="flex flex-col">
                <CpLabel name={`sidecar.envoy.jwt.name`}>Name</CpLabel>
                <CpInput style={{ width: 400 }} name={`sidecar.envoy.jwt.name`} />
                <CpErrorWarning name={`sidecar.envoy.jwt.name`} />
              </div>
              <div className="flex flex-col">
                <CpLabel name={`sidecar.envoy.jwt.issuer`}>Issuer</CpLabel>
                <CpInput style={{ width: 400 }} name={`sidecar.envoy.jwt.issuer`} />
                <CpErrorWarning name={`sidecar.envoy.jwt.issuer`} />
              </div>
            </div>
          </CpFormItem>
          <CpFormItem>
            <div className="flex gap-2">
              <div className="flex flex-col">
                <CpLabel name={`sidecar.envoy.jwt.remote_jwks_http_uri`}>JWKS HTTP URI</CpLabel>
                <CpInput style={{ width: 400 }} name={`sidecar.envoy.jwt.remote_jwks_http_uri`} />
                <CpErrorWarning name={`sidecar.envoy.jwt.remote_jwks_http_uri`} />
              </div>
              <div className="flex flex-col">
                <CpLabel name={`sidecar.envoy.jwt.remote_jwks_http_uri_timeout`}>Default URI Timeout</CpLabel>
                <CpInput
                  style={{ width: 400 }}
                  suffix={`Seconds`}
                  name={`sidecar.envoy.jwt.remote_jwks_http_uri_timeout`}
                />
                <CpErrorWarning name={`sidecar.envoy.jwt.remote_jwks_http_uri_timeout`} />
              </div>
            </div>
          </CpFormItem>
          <CpFormItem>
            <CpLabel name={`skip`}>Excluded Workloads</CpLabel>
            {isGvcCreate ? (
              <CpDescription>Workloads can be excluded after the creation of gvc.</CpDescription>
            ) : (
              <>
                {workloadTableItems.length > 0 ? (
                  <Table<WorkloadBasicTableItem>
                    tableId="workload-draft-auth-workload-items-editable"
                    data={workloadTableItems}
                    styles={{ header: { margin: 0 } }}
                    hideSettings
                    columns={[
                      NameDescriptionNoLinkColumn({ enableSort: false }),
                      GvcColumn(),
                      TagsColumn(),
                      {
                        id: "action",
                        label: "",
                        enableResize: false,
                        size: 120,
                        cell: ({ row }) => {
                          return (
                            <div className="w-full flex justify-end">
                              <Button
                                htmlType={`button`}
                                type={"text"}
                                icon={<CloseOutlined />}
                                danger
                                onClick={() => onRemoveItem(row.index)}
                              />
                            </div>
                          );
                        },
                      },
                    ]}
                  />
                ) : null}
                <TableModal
                  open={isAddingItem}
                  onCancel={() => setIsAddingItem(false)}
                  onOk={() => {
                    onOkAddModal();
                  }}
                >
                  <Table<WorkloadBasicTableItem>
                    tableId={addTableId}
                    title={"Select Workloads to Add"}
                    selectMode="multi"
                    selectKey="selfLink"
                    selections={selectedLinksToAdd}
                    onSelectionsChange={(keys, items) => {
                      setSelectedLinksToAdd(keys);
                      setSelectionItems(items!);
                    }}
                    data={addWorkloadTableItems}
                    headerRenderer={() => {
                      return (
                        <>
                          <TableHeaderRefreshButton onClick={qData.fetchItems} />
                          <CustomTableCPLNQuery
                            tableId={addTableId}
                            kind={addTableKind}
                            onQuery={qData.setQuery}
                            isLoading={qData.isLoadingQueried}
                          />
                        </>
                      );
                    }}
                    enableFilter
                    isLoading={qData.isLoading}
                    onSortChange={qData.setSort}
                    onSearchChange={qData.setSearch}
                    isLoadingMore={qData.isLoadingMore}
                    hasMore={qData.hasMore}
                    onLoadMore={qData.fetchNextPage}
                    columns={[
                      NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
                      GvcColumn(),
                      TagsColumn(),
                    ]}
                  />
                </TableModal>
                <Button
                  htmlType={`button`}
                  className="mt-4"
                  type={"dashed"}
                  icon={<PlusCircleOutlined />}
                  onClick={() => setIsAddingItem(true)}
                >
                  Add Workload
                </Button>
              </>
            )}
            <CpErrorWarning name={`sidecar.envoy.jwt.excludedWorkloads`} />
          </CpFormItem>
          <div style={{ maxWidth: 600 }}>
            <CpArrayString label="Audiences" name={`sidecar.envoy.jwt.audiences`} />
          </div>
          <div style={{ maxWidth: 1200 }}>
            <CpArrayObject
              factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
              labels={["Name", "Value"]}
              name={`sidecar.envoy.jwt.claim_to_headers`}
              label="Claim To Header"
            >
              <CpArrayItemNameValuePair />
            </CpArrayObject>
          </div>
          <div style={{ maxWidth: 1200 }}>
            <CpArrayObjectSortable
              factoryFn={() => ({
                id: uuidv4(),
                chosen: false,
                prefix: "",
                excludeAuth: false,
                headers: [],
              })}
              name={`sidecar.envoy.jwt.rules`}
              label="Path-Based Auth Control"
            >
              <CpArrayItemSidecarJwtPathBasedAuthControl />
            </CpArrayObjectSortable>
          </div>
        </>
      ) : null}
    </>
  );
};
